import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Link } from '@tanstack/react-router';
import {
  projectFormSchema,
  type ProjectFormData,
  type ProjectTemplate,
  DEFAULT_PROJECT_VALUES,
  PROJECT_TEMPLATES,
} from '../utils/validation';
import { getProjectFpsOptions } from '../utils/project-fps';
import { ProjectTemplatePicker } from './project-template-picker';

interface ProjectFormProps {
  onSubmit: (data: ProjectFormData) => Promise<void> | void;
  onCancel?: () => void;
  defaultValues?: Partial<ProjectFormData>;
  isEditing?: boolean;
  isSubmitting?: boolean;
  hideHeader?: boolean;
}

export function ProjectForm({
  onSubmit,
  onCancel,
  defaultValues,
  isEditing = false,
  isSubmitting = false,
  hideHeader = false,
}: ProjectFormProps) {
  const resolvedDefaultValues = useMemo(
    () => ({
      ...DEFAULT_PROJECT_VALUES,
      ...defaultValues,
    }),
    [defaultValues]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset,
  } = useForm<ProjectFormData>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: resolvedDefaultValues,
    mode: 'onChange',
  });

  const matchTemplateId = (width: number, height: number) =>
    PROJECT_TEMPLATES.find((t) => t.width === width && t.height === height)?.id ?? 'custom';

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(() =>
    matchTemplateId(
      resolvedDefaultValues.width,
      resolvedDefaultValues.height
    )
  );

  useEffect(() => {
    reset(resolvedDefaultValues);
  }, [reset, resolvedDefaultValues]);

  useEffect(() => {
    setSelectedTemplateId(
      matchTemplateId(
        resolvedDefaultValues.width,
        resolvedDefaultValues.height
      )
    );
  }, [resolvedDefaultValues.height, resolvedDefaultValues.width]);

  // Declared after the reset effect on purpose: reset() rewrites the input
  // value, which collapses any selection made before it runs.
  useEffect(() => {
    if (!isEditing) {
      const el = document.getElementById('name');
      if (el instanceof HTMLInputElement) {
        el.focus();
        el.select();
      }
    }
  }, [isEditing]);

  const fps = watch('fps');
  const fpsOptions = useMemo(() => getProjectFpsOptions(fps), [fps]);

  const handleSelectTemplate = (template: ProjectTemplate) => {
    setSelectedTemplateId(template.id);
    setValue('width', template.width, { shouldValidate: true });
    setValue('height', template.height, { shouldValidate: true });
    setValue('fps', template.fps, { shouldValidate: true });
  };

  const handleCustomSelect = () => {
    setSelectedTemplateId('custom');
  };

  return (
    <div className="bg-background">
      {/* Header */}
      {!hideHeader && (
        <div className="panel-header border-b border-border">
          <div className="px-6 py-5">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-1">
              {isEditing ? 'Edit Project' : 'Create New Project'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isEditing
                ? 'Update your project settings'
                : 'Set up your video editing workspace'}
            </p>
          </div>
        </div>
      )}

      {/* Form — container queries so the two-column split depends on the
          available width, not the viewport (this form also renders inside
          the narrow edit dialog) */}
      <div className="@container w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid items-start gap-6 @3xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          {/* Project Details */}
          <div className="panel-bg border border-border rounded-lg p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-8 w-1 bg-primary rounded-full" />
              <h2 className="text-lg font-medium text-foreground">Project Details</h2>
            </div>

            <div className="space-y-4">
              {/* Project Name */}
              <div>
                <Label htmlFor="name" className="mb-2 block text-foreground">
                  Project Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  {...register('name')}
                  placeholder="Enter project name..."
                />
                {errors.name && (
                  <p className="mt-1.5 text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description" className="mb-2 block text-foreground">
                  Description
                </Label>
                <Textarea
                  id="description"
                  rows={3}
                  {...register('description')}
                  placeholder="Brief description of your project..."
                />
                {errors.description && (
                  <p className="mt-1.5 text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>
            </div>
          </div>

           {/* Video Settings */}
           <div className="panel-bg border border-border rounded-lg p-5">
             <div className="flex items-center gap-3 mb-4">
               <div className="h-8 w-1 bg-primary rounded-full" />
               <h2 className="text-lg font-medium text-foreground">Video Settings</h2>
             </div>

             <div className="space-y-4">
               {/* Resolution — visual template picker */}
               <div>
                 <p className="text-sm font-medium text-foreground mb-3">Resolution</p>
                 <ProjectTemplatePicker
                   selectedTemplateId={selectedTemplateId === 'custom' ? undefined : selectedTemplateId}
                   onSelectTemplate={handleSelectTemplate}
                   onSelectCustom={handleCustomSelect}
                   isCustomSelected={selectedTemplateId === 'custom'}
                 />
                 {selectedTemplateId === 'custom' && (
                   <div className="mt-4 flex items-center gap-3">
                     <div className="flex-1">
                       <Label htmlFor="width" className="mb-1 block text-xs text-muted-foreground">Width (px)</Label>
                       <Input
                         id="width"
                         type="number"
                         {...register('width', { valueAsNumber: true })}
                          placeholder="1920"
                          min={320}
                        />
                        {errors.width && <p className="mt-1 text-xs text-destructive">{errors.width.message}</p>}
                      </div>
                      <span className="text-muted-foreground mt-4">×</span>
                      <div className="flex-1">
                        <Label htmlFor="height" className="mb-1 block text-xs text-muted-foreground">Height (px)</Label>
                        <Input
                          id="height"
                          type="number"
                          {...register('height', { valueAsNumber: true })}
                          placeholder="1080"
                          min={240}
                        />
                       {errors.height && <p className="mt-1 text-xs text-destructive">{errors.height.message}</p>}
                     </div>
                   </div>
                 )}
               </div>

               {/* Frame Rate */}
               <div>
                 <Label htmlFor="fps" className="mb-2 block text-foreground">
                   Frame Rate
                 </Label>
                 <Select
                   value={fps.toString()}
                   onValueChange={(value) => setValue('fps', Number(value), { shouldValidate: true })}
                 >
                   <SelectTrigger id="fps">
                     <SelectValue />
                   </SelectTrigger>
                   <SelectContent>
                     {fpsOptions.map((preset) => (
                       <SelectItem key={preset.value} value={preset.value.toString()}>
                         {preset.label}
                       </SelectItem>
                     ))}
                   </SelectContent>
                 </Select>
                 {errors.fps && (
                   <p className="mt-1.5 text-sm text-destructive">{errors.fps.message}</p>
                 )}
               </div>
             </div>
           </div>
          </div>

          {/* Actions — primary action dominant, Cancel demoted to ghost */}
          <div className="flex gap-3 justify-end">
            {onCancel ? (
              <Button type="button" variant="ghost" size="lg" disabled={isSubmitting} onClick={onCancel}>
                Cancel
              </Button>
            ) : (
              <Link to="/projects">
                <Button type="button" variant="ghost" size="lg" disabled={isSubmitting}>
                  Cancel
                </Button>
              </Link>
            )}
            <Button type="submit" size="lg" className="min-w-[160px]" disabled={!isValid || isSubmitting}>
              {isSubmitting ? 'Saving...' : isEditing ? 'Update Project' : 'Create Project'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
